import { inject, Injectable } from '@angular/core';
import { createState, select, setProps, Store, withProps } from '@ngneat/elf';
import { excludeKeys, localStorageStrategy, persistState } from '@ngneat/elf-persist-state';
import { selectIsRequestPending, updateRequestStatus, withRequestsStatus } from '@ngneat/elf-requests';
import { catchError, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { AuthCodeResponseGenericResponse } from '../../api/v1/models/auth-code-response-generic-response';
import { CustomerDto } from '../../api/v1/models/customer-dto';
import { CustomerProfileDto } from '../../api/v1/models/customer-profile-dto';
import { CustomerProfileDtoGenericResponse } from '../../api/v1/models/customer-profile-dto-generic-response';
import { OrderStatus } from '../../api/v1/models/order-status';
import { PastOrderDto } from '../../api/v1/models/past-order-dto';
import { RequestRegisterCustomerDto } from '../../api/v1/models/request-register-customer-dto';
import { CustomerService } from '../../api/v1/services/customer.service';
import { BasketService } from '../../api/v1/services/basket.service';
import { OrderFormData } from '../interfaces/order-form-data.interface';
import { LocalstorageService } from '../services/localstorage.service';
import { SessionRepository } from './session.repository';
import { OrderHistoryDto } from '../../api/v1/models/order-history-dto';
import { BasketDto } from '../../api/v1/models/basket-dto';
import { StatusMessageService } from '../services/status-message.service';
import { AuthService } from '../../pages/auth/auth.service';
import { AuthCodeResponse } from '../../api/v1/models/auth-code-response';
import { selectActiveEntity, selectAllEntities, setActiveId, setEntities, withActiveId, withEntities } from '@ngneat/elf-entities';
import { OrderInfoService } from '../../api/v1/services/order-info.service';
import { RegisterData } from '../../layout/fresh/fresh-auth-page/register-form.interface';
import { ConfigRepository } from './config.repository';
import { UnitGenericResponse } from 'src/app/api/v1/models';

interface UIPastOrderDto extends PastOrderDto {
  id: string,
}

interface CustomerModel {
  phone: string | null;
  token: string | null;
  profile: CustomerProfileDto | null;
  orders: PastOrderDto[];
  currentOrder: PastOrderDto | null;
}

const {state: customerState, config: customerStateConfig} = createState(
  withProps<CustomerModel>({
    phone: null,
    token: null,
    profile: null,
    orders: [],
    currentOrder: null,
  }),
  withRequestsStatus<'orderStatusFetching'>(),
  withRequestsStatus<'customerProfile'>(),

  withEntities<UIPastOrderDto>(),
  withActiveId(),
);
const customerStore = new Store({state: customerState, name: 'CustomerStore', config: customerStateConfig});

const customerPersist = persistState(customerStore, {
  key: 'customerState',
  storage: localStorageStrategy,
  source: () => customerStore.pipe(excludeKeys(['profile', 'orders', 'currentOrder'])),
});

@Injectable({providedIn: 'root'})
export class CustomerRepository {
  private customerService = inject(CustomerService);
  private orderInfoService = inject(OrderInfoService);
  private basketService = inject(BasketService);
  private localStorage = inject(LocalstorageService);
  private sessionRepository = inject(SessionRepository);
  private configRepository = inject(ConfigRepository);
  private statusMessage = inject(StatusMessageService);
  private authService = inject(AuthService);

  constructor() {
    customerPersist.initialized$.subscribe(initialized => {
      if(!initialized) {
        return;
      }
      const value = customerStore.getValue();
      if(value.token) {
        this.updateProfile();
      }
    });

    if(this.sessionRepository.onlinePaymentOrderId) {
      this.fetchOrderById(this.sessionRepository.onlinePaymentOrderId).subscribe(data => {
        if(data && data.orderStatus && ~this.resetBasketStatuses.indexOf(data.orderStatus)) {
          this.sessionRepository.removeBasket();
        }
      });
    }
  }

  private readonly resetBasketStatuses = [
    OrderStatus.Creationinitiated,
    OrderStatus.Creationsuccess,
  ];

  isOrderStatusFetching$ = customerStore.pipe(selectIsRequestPending('orderStatusFetching'));

  isLoadingProfile$: Observable<boolean> = customerStore.pipe(selectIsRequestPending('customerProfile'));

  customerProfile$: Observable<CustomerProfileDto | null> = customerStore.pipe(select(state => state.profile));

  orders$: Observable<PastOrderDto[]> = customerStore.pipe(select(state => state.orders));

  ordersNumbers$: Observable<string[]> = customerStore.pipe(select(state => state.orders.map(order => order.number)));

  currentOrderNumber$: Observable<string | undefined> = customerStore.pipe(select(state => state.currentOrder?.number));

  customerBonuses$: Observable<number> = customerStore.pipe(select(state => state.profile?.balance || 0));

  allOrders: Observable<PastOrderDto[] | null> = customerStore.pipe(selectAllEntities());

  activeOrder$: Observable<UIPastOrderDto | undefined> = customerStore.pipe(
    selectActiveEntity()
  );

  activeOrderId$: Observable<string> = this.activeOrder$.pipe(map(category => category?.number || ''));

  orderCustomerData$: Observable<CustomerDto | null> = customerStore.pipe(
    select(state => state.profile),
    map(profile => {
      const savedData = this.getSavedOrderFormData();

      if(profile) {
        return {
          name: profile.name || savedData?.customer?.name || null,
          phone: profile.phone,
          email: profile.email,
          token: profile.token,
        };
      }

      if(savedData) {
        return savedData.customer;
      }

      return null;
    })
  );

  get customerProfile(): CustomerProfileDto | null {
    return customerStore.query(state => state.profile);
  }

  get customerToken(): string | null | undefined {
    return customerStore.query(state => state.token);
  }

  get customerId(): string | null | undefined {
    return customerStore.query(state => state.profile?.id);
  }

  get customerBalance(): number {
    return customerStore.query(state => state.profile?.balance || 0);
  }

  token$: Observable<string | null> = customerStore.pipe(select(state => state.token));
  tokenRefresh$ = new Subject<string | null>();

  requestAuthCode(phone: string): Observable<AuthCodeResponse> {
    customerStore.update(state => ({...state, phone}));
    return this.customerService.apiV1CustomerRequestAuthCodePost$Json({body: {phone}}).pipe(
      map(data => {
        if(data.error || !data.result) {
          throw data;
        }
        return data.result;
      }),
      catchError(error => {
        throw error;
      })
    );
  }

  requestProfile(code: string): Observable<CustomerProfileDto | null> {
    const phone = customerStore.query((state) => state.phone);
    if(!phone) {
      return of(null);
    }

    return this.customerService.apiV1CustomerSessionAuthPost$Json({body: {code, phone: phone}}).pipe(
      map(data => {
        if(data.error) {
          throw data;
        }
        return data.result;
      }),
      tap((customerProfile: CustomerProfileDto | null) => {
        const token = customerProfile?.token ?? null;
        customerStore.update(state => ({...state, profile: customerProfile, token: token}));

        this.tokenRefresh$.next(token);
      })
    );
  }

  fetchProfile(): Observable<CustomerProfileDto | null> {
    customerStore.update(updateRequestStatus('customerProfile', 'pending'));

    return this.token$.pipe(
      switchMap(token => {
        if(!token) {
          return this.tokenRefresh$;
        }
        return this.token$;
      }),
      filter(token => !!token),
      switchMap(token =>
        this.customerService.apiV1CustomerSessionFetchPost$Json(
          {
            ignoreCache: true,
            body: {token: token as string}
          })
      ),
      map(data => data.result),
      tap(profile => {
        customerStore.update(updateRequestStatus('customerProfile', 'success'));
        customerStore.update(state => ({...state, profile: profile}));

        if(!profile) {
          customerStore.update(state => ({...state, token: null}));
        }
      }),
      catchError(error => {
        customerStore.update(state => ({...state, token: null}));
        customerStore.update(updateRequestStatus('customerProfile', 'error', error));
        throw error;
      })
    );
  }

  updateProfile(): void {
    this.fetchProfile().subscribe();
  }

  fetchOrderHistory(offset: number = 0, limit: number = 10): Observable<OrderHistoryDto> {
    return this.token$.pipe(
      switchMap(token => {
        if(!token) {
          return this.tokenRefresh$;
        }
        return this.token$;
      }),
      filter(token => !!token),
      switchMap(token => this.customerService.apiV1CustomerOrdersHistoryPost$Json({body: {token: token as string, offset, limit}})),
      map(response => {
        const ordersList: UIPastOrderDto[] = [];
        if(response.result) {

          response.result.orders.forEach(order => {
            ordersList.push({
              ...order,
              id: order.number,
            });
          });

          customerStore.update(
            setEntities(ordersList),
            setActiveId(ordersList.length ? ordersList[0].number : null)
          );
          return response.result;
        }
        throw response;
      })
    );
  }

  burnSession(): Observable<UnitGenericResponse> {
    return this.token$.pipe(
      filter((token): token is string => !!token),
      switchMap(token => this.customerService.apiV1CustomerSessionBurnPost$Json({body: {token}})),
      tap(() => {
        customerStore.update(setProps({profile: null, token: null, orders: [], email: null}));
      })
    );
  }

  saveOrderFormData(orderData: OrderFormData): void {
    this.localStorage.set('ordering_form_data', orderData);
  }

  getSavedOrderFormData(): OrderFormData | null {
    const savedFormData = this.localStorage.get('ordering_form_data');

    if(!savedFormData) {
      return null;
    }
    return savedFormData;
  }

  updateCustomerProfile(
    email: string,
    name: string,
    birthday: string | null
  ): Observable<CustomerProfileDtoGenericResponse | null> {
    const storedProfile = customerStore.query(state => state.profile);
    if(storedProfile === null) {
      return of(null);
    }

    const profile: CustomerProfileDto = {
      email: email.toLowerCase(),
      name: name,
      birthday: birthday,
      id: storedProfile.id,
      phone: storedProfile.phone,
      authToken: storedProfile.authToken,
      token: storedProfile.token
    };

    return this.customerService.apiV1CustomerUpdatePost$Json({body: profile}).pipe(
      map(value => {
        if(value.error === null) {
          customerStore.update(state => ({...state, profile: value.result}));
        }
        return value;
      })
    );
  }

  register(registerData: RegisterData): Observable<AuthCodeResponseGenericResponse> {
    const request: RequestRegisterCustomerDto = {
      email: registerData.email.toLowerCase(),
      phone: registerData.phone,
      name: registerData.name,
      storeId: this.configRepository.activeOutletId,
      acceptedPersonalDataProcessingPolicy: registerData.acceptedPersonalDataProcessingPolicy,
      acceptedMarketingCommunications: registerData.acceptedMarketingCommunications,
    };
    return this.customerService.apiV1CustomerRegisterPost$Json({body: request});
  }

  setCurrentOrder(order: PastOrderDto): void {
    customerStore.update(state => ({
      ...state,
      currentOrder: order,
    }));
  }

  fetchOrderById(id: string): Observable<PastOrderDto> {
    customerStore.update(updateRequestStatus('orderStatusFetching', 'pending'));

    return this.orderInfoService.apiV1OrdersInfoOrderIdGet$Json({orderId: id}).pipe(
      map(response => {
        if(response.result && response.result.orders) {
          return response.result.orders;
        }

        throw response;
      }),
      map(orders => orders.length ? orders[0] : null),
      map(orderInfo => {
        if(!orderInfo) {
          return customerStore.query(state => state.currentOrder);
        }
        return orderInfo;
      }),
      map(orderInfo => {
        if(!orderInfo) {
          throw new Error(`fetchOrderById: Order with id ${id} is not found`);
        }
        return orderInfo;
      }),

      tap(() => customerStore.update(updateRequestStatus('orderStatusFetching', 'success'))),

      catchError(error => {
        customerStore.update(updateRequestStatus('orderStatusFetching', 'error', error));
        throw error;
      })
    );
  }

  restoreOrder(basketId: string): Observable<BasketDto | null> {
    return this.getTokenWithAuthDialog().pipe(
      filter(token => !!token),
      switchMap(token => this.fetchBasketById(basketId, token!)),

      tap(basket => {
        if(!basket) {
          return;
        }
        this.sessionRepository.removeBasket();

        this.sessionRepository.addReplayItems(basket.storedItems);
        this.sessionRepository.setPreprocessBasketId(basketId);
      })
    );
  }

  fetchBasketById(basketId: string, token: string): Observable<BasketDto | null> {
    return this.basketService.apiV1BasketBasketIdPost$Json({
      basketId: basketId,
      body: {token: token!},
    }).pipe(
      map(response => {
        if(response.error) {
          throw new Error('basket is not found');
        }
        return response.result;
      })
    );
  }

  private getTokenWithAuthDialog(): Observable<string | null> {
    return this.token$.pipe(
      switchMap(token => {
        if(token) {
          return this.token$;
        }

        this.statusMessage.setStatusMessage({
          title: $localize`:@@MessageModalTitleWarning:Warning`,
          message: $localize`:@@MessageAuthWarning:Log in to access your cart`,
          type: 'warning',
          dialog: {
            buttons: [
              {type: 'accent', text: $localize`:@@ToAuth:To auth`, onClick: () => this.statusMessage.setDialogState('accept')},
              {type: 'warn', text: $localize`:@@Refuse:Refuse`, onClick: () => this.statusMessage.setDialogState('deny')},
            ]
          }
        });

        return this.statusMessage.dialogSubj$.pipe(
          filter(dialogStatus => dialogStatus !== 'deny'),
          tap(() => {
            this.authService.showAuthModal();
            this.authService.setRedirectToPersonalPage(false);
          }),
          switchMap(() => this.tokenRefresh$),
        );
      })
    );
  }

  setActiveOrder(id: string): void {
    customerStore.update(setActiveId(id));
  }
}

